import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { ToastService } from '@core/services/toast.service';
import { AffiliateApiService } from './affiliate-api.service';
import {
  PAGE_REQUEST_DEFAULT_PAGE_NUMBER,
  PAGE_REQUEST_DEFAULT_PAGE_SIZE,
  PageRequest,
  normalizePageRequest,
} from '@core/models/common.model';
import {
  AffiliateResponse,
  AffiliateEligibilityResponse,
  AffiliateListItemResponse,
  AffiliateTransactionHistoryResponse,
  CreateAffiliateRequest,
  UpdateAffiliateRequest,
} from './affiliate.model';

@Injectable({ providedIn: 'root' })
export class AffiliateStore {
  private readonly affiliateApiService = inject(AffiliateApiService);
  private readonly toastService = inject(ToastService);

  readonly affiliates = signal<AffiliateListItemResponse[]>([]);
  readonly affiliatesSearch = signal('');
  readonly affiliatesTotalCount = signal(0);
  readonly affiliatesPageNumber = signal(PAGE_REQUEST_DEFAULT_PAGE_NUMBER);
  readonly affiliatesPageSize = signal(PAGE_REQUEST_DEFAULT_PAGE_SIZE);
  readonly affiliatesTotalPages = signal(0);
  readonly selectedAffiliate = signal<AffiliateResponse | null>(null);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly deactivating = signal(false);
  readonly activating = signal(false);
  readonly history = signal<AffiliateTransactionHistoryResponse[]>([]);
  readonly affiliateLookupResponses = signal<AffiliateListItemResponse[]>([]);
  readonly affiliateLookupLoading = signal(false);
  readonly eligibleAffiliates = signal<AffiliateEligibilityResponse[]>([]);
  readonly eligibleAffiliatesLoading = signal(false);

  loadAffiliates(pageRequest?: PageRequest, search?: string | null): void {
    const normalizedPageRequest = normalizePageRequest({
      pageNumber: pageRequest?.pageNumber ?? this.affiliatesPageNumber(),
      pageSize: pageRequest?.pageSize ?? this.affiliatesPageSize(),
    });

    if (search !== undefined) {
      this.affiliatesSearch.set(search?.trim() ?? '');
    }

    this.loading.set(true);
    this.affiliateApiService
      .getAffiliates(normalizedPageRequest, this.affiliatesSearch())
      .subscribe({
        next: (response) => {
          this.affiliates.set(response.items);
          this.affiliatesTotalCount.set(response.totalCount);
          this.affiliatesPageNumber.set(response.pageNumber);
          this.affiliatesPageSize.set(response.pageSize);
          this.affiliatesTotalPages.set(response.totalPages);
          this.loading.set(false);
        },
        error: (error: HttpErrorResponse) => {
          this.toastService.error('Failed to load affiliates');
          this.loading.set(false);
        },
      });
  }

  loadAffiliate(affiliateId: string): void {
    this.loading.set(true);
    this.affiliateApiService.getAffiliate(affiliateId).subscribe({
      next: (affiliate) => {
        this.selectedAffiliate.set(affiliate);
        this.loading.set(false);
      },
      error: (error: HttpErrorResponse) => {
        this.toastService.error('Failed to load affiliate');
        this.loading.set(false);
      },
    });
  }

  loadAffiliateHistory(affiliateId: string): void {
    this.affiliateApiService.getAffiliateHistory(affiliateId).subscribe({
      next: (history) => this.history.set(history),
      error: () => this.toastService.error('Failed to load affiliate history'),
    });
  }

  loadAffiliateLookupResponses(search?: string | null): void {
    this.affiliateLookupLoading.set(true);
    this.affiliateApiService.getAffiliates({ pageNumber: 1, pageSize: 20 }, search).subscribe({
      next: (response) => {
        this.affiliateLookupResponses.set(response.items);
        this.affiliateLookupLoading.set(false);
      },
      error: () => {
        this.toastService.error('Failed to load affiliates');
        this.affiliateLookupLoading.set(false);
      },
    });
  }

  loadEligibleAffiliates(interventionTypeId: string): void {
    this.eligibleAffiliatesLoading.set(true);
    this.affiliateApiService.getEligibleAffiliates(interventionTypeId).subscribe({
      next: (response) => {
        this.eligibleAffiliates.set(response);
        this.eligibleAffiliatesLoading.set(false);
      },
      error: () => {
        this.toastService.error('Failed to load eligible affiliates');
        this.eligibleAffiliatesLoading.set(false);
      },
    });
  }

  createAffiliate(request: CreateAffiliateRequest, onComplete?: () => void): void {
    this.saving.set(true);
    this.affiliateApiService.createAffiliate(request).subscribe({
      next: (affiliate) => {
        this.toastService.success('Affiliate created successfully');
        this.saving.set(false);
        if (onComplete) {
          onComplete();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.toastService.error('Failed to create affiliate');
        this.saving.set(false);
      },
    });
  }

  updateAffiliate(affiliateId: string, request: UpdateAffiliateRequest, onComplete?: () => void): void {
    this.saving.set(true);
    this.affiliateApiService.updateAffiliate(affiliateId, request).subscribe({
      next: (affiliate) => {
        this.selectedAffiliate.set(affiliate);
        this.loadAffiliateHistory(affiliateId);
        this.toastService.success('Affiliate updated successfully');
        this.saving.set(false);
        if (onComplete) {
          onComplete();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.toastService.error('Failed to update affiliate');
        this.saving.set(false);
      },
    });
  }

  deactivateAffiliate(affiliateId: string, onComplete?: () => void): void {
    this.deactivating.set(true);
    this.affiliateApiService.deactivateAffiliate(affiliateId).subscribe({
      next: (affiliate) => {
        this.selectedAffiliate.set(affiliate);
        this.loadAffiliateHistory(affiliateId);
        this.toastService.success('Affiliate deactivated successfully');
        this.deactivating.set(false);
        if (onComplete) {
          onComplete();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.toastService.error('Failed to deactivate affiliate');
        this.deactivating.set(false);
      },
    });
  }

  activateAffiliate(affiliateId: string, onComplete?: () => void): void {
    this.activating.set(true);
    this.affiliateApiService.activateAffiliate(affiliateId).subscribe({
      next: (affiliate) => {
        this.selectedAffiliate.set(affiliate);
        this.loadAffiliateHistory(affiliateId);
        this.toastService.success('Affiliate activated successfully');
        this.activating.set(false);
        if (onComplete) {
          onComplete();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.toastService.error('Failed to activate affiliate');
        this.activating.set(false);
      },
    });
  }
}
